import { FormLabel } from '@/components/FormLabel'
import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import { EditFilled } from '@/icons'
import { Box, Checkbox, Flex, Grid, IconButton, Text, Tooltip, useDisclosure, useOutsideClick } from '@chakra-ui/react'
import type { AddonDescriptionProps, OfferPackage, OfferProperty, OfferPropertyAddition, OfferTravelAddition, PackageItemModel, Part } from 'BrokerTsLib/src/dtos'
import { BikeKinds, PetKinds } from 'BrokerTsLib/src/dtos'
import { Insurer, PackageItemLimitType, Risk } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import { useMemo, useRef, useState } from 'react'
import { AdditionValueEditor } from './AdditionValueEditor'

type AddonCheckboxProps = {
  addon: PackageItemModel
  disabled: boolean
  offer: OfferPackage | OfferProperty
  changedPart?: Part
  onChange: (checked: boolean, limit?: number, packageItemVariantId?: number) => void
  onHover?: (data: AddonDescriptionProps | null) => void
}

export function AddonCheckbox({
  addon,
  onHover,
  offer,
  changedPart,
  onChange,
  ...props
}: AddonCheckboxProps) {
  const { isOpen, onToggle, onClose } = useDisclosure()
  const { app } = useApp()


  const offerPart = changedPart ?? offer.parts.find(p => p.packageItem?.packageItemId == addon.packageItemId)
  const part = useMemo(() => {
    return changedPart ?? offerPart
  }, [changedPart, offerPart])

  const defaultChecked = part?.included ?? false

  const [checked, setChecked] = useState(defaultChecked)

  const valueColor = checked ? undefined : 'gray.300'

  const specificValueLabel: React.ReactNode | undefined = useGetSpecificDisplayValue(app, offer.insurerId, addon, valueColor)
  const [changedIncluded, setChangedIncluded] = useState(defaultChecked)

  const handleEditClick = () => {
    onToggle()
  }

  const ref = useRef(null)
  useOutsideClick({
    ref: ref,
    handler: () => onClose(),
  })

  const [hovered, setHovered] = useState(false)

  const isSpecificRisk =
    (offer.insurerId == Insurer.Direct && addon.riskId == Risk.ADDITION_PET)
    || (offer.insurerId == Insurer.Direct && addon.riskId == Risk.ADDITION_BICYCLE)
    || ([Insurer.Direct, Insurer.GeneraliCeskaPojistovna].includes(offer.insurerId) && addon.riskId == Risk.ADDITION_MEDICAL)
    || ([Insurer.GeneraliCeskaPojistovna].includes(offer.insurerId) && addon.riskId == Risk.ADDITION_MUSICAL_INSTR)
    || ([Insurer.GeneraliCeskaPojistovna].includes(offer.insurerId) && addon.riskId == Risk.ADDITION_CONSTRUCTION_ASSEMBLY)
    || (offer.insurerId == Insurer.Direct && addon.riskId == Risk.ADDITION_TRAVEL_SPORT_EQUIPMENT)
    || (offer.insurerId == Insurer.Direct && addon.riskId == Risk.ADDITION_MINI_KASKO)
    || (offer.insurerId == Insurer.Direct && addon.riskId == Risk.ADDITION_TRAVEL_PET)

  const showEditButton = (addon.limitType == PackageItemLimitType.Limit || isSpecificRisk)
    && addon.addable
    && checked
    && (addon.variants.length > 1 || specificValueLabel)

  let defaultLImit = part?.limit
  if (!defaultLImit) {
    const v = part?.packageItem?.variants ?? addon.variants
    defaultLImit = v.find(x => x.isDefault)?.defaultLimit ?? v.length > 0 ? v[0].defaultLimit : undefined
  }

  return (
    <Grid
      ref={ref}
      bgColor={hovered ? 'gray.100' : undefined}
      sx={{
        mx: -4,
        px: 4,
        py: 2,
        gridTemplateColumns: '5fr 4fr',
        justifyContent: 'start',
        justifyItems: 'start',
        borderRadius: 'sm'
      }}
      onMouseEnter={() => {
        onHover?.({
          label: addon.riskName,
          description: addon.description ? <Box dangerouslySetInnerHTML={{ __html: addon.description }}></Box> : undefined,
          value: checked ? 'Zahrnuto' : 'Není zahrnuto'
        })
        setHovered(true)
      }}
      onMouseLeave={() => setHovered(false)}
    >
      <FormLabel pr={2}>
        {addon.riskName}
      </FormLabel>

      {!isOpen &&
        <Grid
          sx={{
            gridTemplateColumns: '12px 16px auto',
            columnGap: 4
          }}
        >
          <Checkbox
            size="sm"
            isChecked={checked}
            disabled={props.disabled || !addon.addable}
            onChange={e => {
              setChangedIncluded(true)
              const checked = e.target.checked

              setChecked(checked)

              if ((addon.limitType == PackageItemLimitType.Limit && !showEditButton) && checked) {
                if (addon.variants.length == 1 && !specificValueLabel) {
                  const variant = addon.variants[0]
                  onChange(checked, variant.defaultLimit, variant.id)
                } else {
                  handleEditClick()
                }
              } else {
                onChange(checked)

                if (isSpecificRisk && checked)
                  handleEditClick()
              }
            }}
          />

          <Flex alignItems='center'>
            {showEditButton &&
              <Tooltip label="Upravit pojištění" hasArrow placement="top">
                <IconButton variant="ghost" aria-label="Nastavení" onClick={handleEditClick}>
                  <EditFilled color="gray.500" width={4} height={4} />
                </IconButton>
              </Tooltip>
            }
          </Flex>

          {specificValueLabel && changedIncluded && specificValueLabel}
          {!specificValueLabel && defaultLImit && addon.limitType == PackageItemLimitType.Limit && // TODO: kdyz neni part, tak zobrazovat default variant z addon
            <FormLabel color={valueColor}>{priceFormat(defaultLImit)}</FormLabel>
          }
        </Grid>
      }

      {(changedIncluded || checked) &&
        <AdditionValueEditor
          isOpen={isOpen}
          addon={addon}
          offer={offer}
          part={part}
          onChange={(limit, packageItemVariantId) => {
            onChange(checked, limit, packageItemVariantId)
            onClose()
          }}
        />
      }
    </Grid>
  )
}

function useGetSpecificDisplayValue(
  app: Apps | null,
  insurerId: number,
  addon: PackageItemModel,
  valueColor: string | undefined
) {
  const {
    watch
  } = useFormContext<OfferPropertyAddition | OfferTravelAddition>()

  let value: React.ReactNode | undefined = undefined
  const isProperty = app == Apps.property
  const isTravel = app == Apps.travel

  const additionData = watch()

  if (isProperty) {
    const additionDataProperty = additionData as OfferPropertyAddition

    if (insurerId == Insurer.Direct && addon.riskId == Risk.ADDITION_PET) {
      value = (
        <Flex flexWrap='wrap' color={valueColor} alignItems='center' columnGap={4}>
          {additionDataProperty?.pets?.map((item, index) => {
            return (
              <Flex flexWrap='nowrap' key={`${item.offerPartId}_${index}`} alignItems='center' gap={1}>
                {item.limit && <Text fontWeight='medium'>{priceFormat(item.limit)}</Text>}
                <Text fontSize='sm'>({PetKinds[item.type]}, {item.race})</Text>
              </Flex>
            )
          })}
        </Flex>
      )
    }
    else if (insurerId == Insurer.Direct && addon.riskId == Risk.ADDITION_BICYCLE) {
      value = (
        <Flex flexWrap='wrap' color={valueColor} alignItems='center' columnGap={4}>
          {additionDataProperty?.bicycles?.map((item, index) => {
            return (
              <Flex flexWrap='nowrap' key={`${item.offerPartId}_${index}`} alignItems='center' gap={1}>
                {item.limit && <Text fontWeight='medium'>{priceFormat(item.limit)}</Text>}
                <Text fontSize='sm'>({[BikeKinds[item.bikeType], ...(item.brand ? [item.brand] : [])].join(', ')})</Text>
              </Flex>
            )
          })}
        </Flex>
      )
    }
    else if ([Insurer.Direct, Insurer.GeneraliCeskaPojistovna].includes(insurerId) && addon.riskId == Risk.ADDITION_MEDICAL) {
      value = (
        <Flex flexWrap='wrap' color={valueColor} alignItems='center' columnGap={4}>
          {additionDataProperty?.medicals?.map((item, index) => {
            return (
              <Flex flexWrap='nowrap' key={`${item.offerPartId}_${index}`} alignItems='center' gap={1}>
                {item.limit && <Text fontWeight='medium'>{priceFormat(item.limit)}</Text>}
                <Text fontSize='sm'>({item.type ?? item.description})</Text>
              </Flex>
            )
          })}
        </Flex>
      )
    }
    else if ([Insurer.GeneraliCeskaPojistovna].includes(insurerId) && addon.riskId == Risk.ADDITION_MUSICAL_INSTR) {
      value = (
        <Flex flexWrap='wrap' color={valueColor} alignItems='center' columnGap={4}>
          {additionDataProperty?.musicalInstruments?.map((item, index) => {
            return (
              <Flex flexWrap='nowrap' key={`${item.offerPartId}_${index}`} alignItems='center' gap={1}>
                {item.limit && <Text fontWeight='medium'>{priceFormat(item.limit)}</Text>}
                <Text fontSize='sm'>({item.description})</Text>
              </Flex>
            )
          })}
        </Flex>
      )
    }
  }

  else if (isTravel) {
    const additionDataTravel = additionData as OfferTravelAddition

    if (insurerId == Insurer.Direct && addon.riskId == Risk.ADDITION_TRAVEL_SPORT_EQUIPMENT) {
      value = (
        <Flex flexWrap='wrap' color={valueColor} alignItems='center' columnGap={4}>
          {additionDataTravel?.sportEquipments?.map((item, index) => {
            return (
              <Flex flexWrap='nowrap' key={`${item.offerPartId}_${index}`} alignItems='center' gap={1}>
                <Text fontSize='sm'>{item.description}</Text>
              </Flex>
            )
          })}
        </Flex>
      )
    }

    if (insurerId == Insurer.Direct && addon.riskId == Risk.ADDITION_MINI_KASKO) {
      value = (
        <Flex flexWrap='wrap' color={valueColor} alignItems='center' columnGap={4}>
          {additionDataTravel?.vehicles?.map((item, index) => {
            return (
              <Flex flexWrap='nowrap' key={`${item.offerPartId}_${index}`} alignItems='center' gap={1}>
                <Text fontSize='sm'>{item.registrationMark}</Text>
              </Flex>
            )
          })}
        </Flex>
      )
    }

    if (insurerId == Insurer.Direct && addon.riskId == Risk.ADDITION_TRAVEL_PET) {
      value = (
        <Flex flexWrap='wrap' color={valueColor} alignItems='center' columnGap={4}>
          {additionDataTravel?.pets?.map((item, index) => {
            return (
              <Flex flexWrap='nowrap' key={`${item.offerPartId}_${index}`} alignItems='center' gap={1}>
                <Text fontSize='sm'>{item.chipId}</Text>
              </Flex>
            )
          })}
        </Flex>
      )
    }
  }

  return value
}